import { ReactNode } from "react";
import { models } from "types/api/viewModels.ts";
import {
	OrderDetailsLineItemCashValueColumn,
	OrderDetailsLineItemDescription,
	OrderDetailsLineItemDescriptionRow,
	OrderDetailsLineItemDescriptionSpacer,
	OrderDetailsLineItemLabel,
	OrderDetailsLineItemModDescription,
	OrderDetailsLineItemModNumber,
	OrderDetailsLineItemModSkuCode,
	OrderDetailsLineItemPromotionLabel,
	OrderDetailsLineItemPromotionValue,
	OrderDetailsLineItemQuantity,
	OrderDetailsLineItemRowModSku,
	OrderDetailsLineItemRowSku,
	OrderDetailsLineItemSkuCode,
	OrderDetailsLineItemSkuRow,
	OrderDetailsLineItemValueColumn,
	OrderDetailsLineItemValuePrimary,
	OrderDetailsLineItemValueSecondary
} from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/OrderDetailsLineItemGrid.styles.ts";
import {
	AdjustedListPriceText,
	FinalPriceText,
	HingeText,
	OrderReplacementText,
	PartsAddedText,
	QuantityText,
	StandardListPriceText
} from "constants/text.ts";
import { formatPriceWithPrefix } from "utils/string.ts";
import { useOrderDetailsLineItemGrid } from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/useOrderDetailsLineItemGrid.tsx";
import { Button } from "@mui/material";
import {
	LineAddedMessage,
	ReplacementControls,
	StyledCheckCircleOutlineIcon
} from "components/OrderDetailsLineItem/OrderDetailsLineItem.styles.ts";
import { AcknowledgementPriceFormatType } from "types/declarations/enums.ts";

interface ItemRow {
	gridColumn: number;
	content?: ReactNode;
}

// fills in undefined columns with empty divs
export const normalizeGridRow = (rows: ItemRow[]): ItemRow[] => {
	return Array.from({ length: 7 }, (_, i) => ({
		gridColumn: i,
		content: rows.find((row) => row.gridColumn === i)?.content || <div />
	}));
};

const formatWithOperator = (value: number | null | undefined) => {
	const operator = String(value).includes("-") ? "-" : "+";
	const prefix = value ? operator : "";
	return `${prefix}${formatPriceWithPrefix(value).replaceAll("-", "")}`;
};

// hinge and qty
const LineItemCountColumn = ({ label, value }: { label: string; value: ReactNode }) => (
	<OrderDetailsLineItemValueColumn>
		<OrderDetailsLineItemValuePrimary>{label}</OrderDetailsLineItemValuePrimary>
		<OrderDetailsLineItemValueSecondary>{value}</OrderDetailsLineItemValueSecondary>
	</OrderDetailsLineItemValueColumn>
);

// list price, net price, adjusted price, extended price, etc.
const PricingColumn = ({ label, value }: { label: string; value: number | null | undefined }) => (
	<OrderDetailsLineItemCashValueColumn>
		<OrderDetailsLineItemValuePrimary>{label}</OrderDetailsLineItemValuePrimary>
		<OrderDetailsLineItemValueSecondary>
			{formatPriceWithPrefix(value, false, true)}
		</OrderDetailsLineItemValueSecondary>
	</OrderDetailsLineItemCashValueColumn>
);

// first row (sku, hinge, qty, list price, etc.)
export const useItemRow = (
	item: models["LineItemViewModel"] | models["PendingLineItemViewModel"],
	isAcknowledgement?: boolean,
	isReplacement?: boolean,
	handleOnClick?: () => void,
	billTo?: models["BillToViewModel"] | null,
	isNewQuoteOrder?: boolean,
	ackPermissionOverride?: AcknowledgementPriceFormatType | null
): ItemRow[] => {
	const { extendedPrice, isPartAdded } = useOrderDetailsLineItemGrid(item, billTo, ackPermissionOverride);
	const itemPricing = "pricing" in item ? item.pricing : item;
	const hasNoMods =
		(!item.modifications || item.modifications.length === 0) &&
		(!item.requiredDimensions || item.requiredDimensions.length === 0) &&
		(!item.requiredCategories || item.requiredCategories.length === 0);

	return [
		{
			gridColumn: 0,
			content: (
				<OrderDetailsLineItemRowSku>
					<OrderDetailsLineItemSkuRow>
						<OrderDetailsLineItemQuantity>{item.lineItemNumber}</OrderDetailsLineItemQuantity>
						<OrderDetailsLineItemSkuCode>{item.sku}</OrderDetailsLineItemSkuCode>
					</OrderDetailsLineItemSkuRow>
					<OrderDetailsLineItemDescriptionRow>
						<OrderDetailsLineItemDescriptionSpacer />
						<OrderDetailsLineItemDescription>{item.description}</OrderDetailsLineItemDescription>
					</OrderDetailsLineItemDescriptionRow>
				</OrderDetailsLineItemRowSku>
			)
		},
		{
			gridColumn: 1,
			content: "hinge" in item && item.hinge && (
				<LineItemCountColumn
					label={HingeText}
					value={item.hinge}
				/>
			)
		},
		{
			gridColumn: 2,
			content: (
				<LineItemCountColumn
					label={QuantityText}
					value={item.quantityOrdered}
				/>
			)
		},
		{
			gridColumn: 3,
			content: (
				<PricingColumn
					label={StandardListPriceText}
					value={itemPricing?.listPrice}
				/>
			)
		},
		{
			gridColumn: 4,
			content: extendedPrice?.priceValue !== null &&
				extendedPrice?.priceValue !== undefined &&
				(hasNoMods || isNewQuoteOrder || !isAcknowledgement) && (
					<PricingColumn
						label={extendedPrice.priceLabel}
						value={extendedPrice.priceValue}
					/>
				)
		},
		{
			gridColumn: 5,
			content: isReplacement && (
				<ReplacementControls>
					{"isReplacementEligible" in item && item.isReplacementEligible && (
						<Button
							onClick={handleOnClick}
							variant="outlined"
							data-testid="line-item-order-replacement-button"
						>
							{OrderReplacementText}
						</Button>
					)}
					{isPartAdded && (
						<LineAddedMessage>
							<StyledCheckCircleOutlineIcon size={20} />
							<p>{PartsAddedText}</p>
						</LineAddedMessage>
					)}
				</ReplacementControls>
			)
		}
	];
};

// mod rows
export const getModificationRow = (
	mod: models["PendingModificationViewModel"],
	isAcknowledgement?: boolean,
	isReplacement?: boolean
): ItemRow[] => {
	return [
		{
			gridColumn: 0,
			content: (
				<OrderDetailsLineItemRowModSku>
					<OrderDetailsLineItemModNumber>{mod.lineItemNumber}</OrderDetailsLineItemModNumber>
					<OrderDetailsLineItemModSkuCode>
						{mod.sku} - {mod.description}
					</OrderDetailsLineItemModSkuCode>
					{mod.values?.map((value) => (
						<OrderDetailsLineItemModDescription key={value.value}>
							{value.value}&quot;
						</OrderDetailsLineItemModDescription>
					))}
				</OrderDetailsLineItemRowModSku>
			)
		},
		{
			gridColumn: 2,
			content: !isReplacement && isAcknowledgement && (
				<OrderDetailsLineItemLabel>{formatWithOperator(mod.listPrice)}</OrderDetailsLineItemLabel>
			)
		}
	];
};

// extended/adjusted price rows
export const usePricingRow = (
	item: models["LineItemViewModel"] | models["PendingLineItemViewModel"],
	billTo?: models["BillToViewModel"] | null,
	ackPermissionOverride?: AcknowledgementPriceFormatType | null
): ItemRow[] => {
	const { extendedPrice } = useOrderDetailsLineItemGrid(item, billTo, ackPermissionOverride);
	const isNewOrderPrice = !("pricing" in item);

	return [
		{
			gridColumn: 4,
			content: (
				<PricingColumn
					label={AdjustedListPriceText}
					value={isNewOrderPrice ? item.listPrice : item.pricing?.adjustedListPrice}
				/>
			)
		},
		{
			gridColumn: 5,
			content: extendedPrice && (
				<PricingColumn
					label={extendedPrice.priceLabel}
					value={extendedPrice.priceValue}
				/>
			)
		}
	];
};

// promotions rows
export const getPromotionRow = (promotion: models["PromotionViewModel"]): ItemRow[] => {
	return [
		{
			gridColumn: 3,
			content: <OrderDetailsLineItemPromotionLabel>{promotion.promotionName}</OrderDetailsLineItemPromotionLabel>
		},
		{
			gridColumn: 4,
			content: (
				<OrderDetailsLineItemPromotionValue>
					{formatWithOperator(promotion.promotionAmount)}
				</OrderDetailsLineItemPromotionValue>
			)
		}
	];
};

// final price row
export const getFinalPriceRow = (finalPrice: number | undefined | null): ItemRow[] => {
	return [
		{
			gridColumn: 3,
			content: <OrderDetailsLineItemPromotionLabel>{FinalPriceText}</OrderDetailsLineItemPromotionLabel>
		},
		{
			gridColumn: 4,
			content: (
				<OrderDetailsLineItemPromotionValue>
					{formatPriceWithPrefix(finalPrice, false, true)}
				</OrderDetailsLineItemPromotionValue>
			)
		}
	];
};
